import { api } from "../api/api";
import Box from "@mui/material/Box";
import * as routes from "../data/links/routes";
import { useNavigate } from "react-router-dom";
import Loader from "../components/Display/Loader";
import { errorTexts } from "../data/assets/texts";
import * as endpoints from "../data/links/endpoints";
import { AllCenteredColumn } from "../data/style/Style";
import { useNotifications } from "./NotificationContext";
import { createContext, useState, useContext } from "react";
import { getSettings, getUser, getAthleteProfile } from "../api/user";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  loginUser,
  register,
  recoverPassword,
  resetPassword,
} from "../api/auth";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  let navigate = useNavigate();
  const client = useQueryClient();
  const { enableNotification } = useNotifications();
  const [activeRequest, setActiveRequest] = useState(false);
  const [registerModalActive, setRegisterModalActive] = useState(false);
  const [userId, setUserId] = useState(null);

  let logoutUser = () => {
    api
      .get(endpoints.LOGOUT_USER)
      .then((response) => {
        navigate(routes.INDEX);
        client.removeQueries(["user", user.id]);
        client.removeQueries(["profile", user.id]);
        client.removeQueries(["settings", user.id]);
      })
      .catch((error) => {
        if (error.response.status >= 500) {
          enableNotification(errorTexts.system_error, "error");
        }
      });
  };

  const { mutate: createAccount } = useMutation({
    mutationFn: register,
    onSuccess: (response) => {
      enableNotification(response.data.message, "success");
      setRegisterModalActive(false);
    },
    onError: (error) => {
      if (error.response.status === 409 || error.response.status === 406) {
        enableNotification(error.response.data.message, "info");
        return;
      }

      if (error.response.status === 400 || error.response.status >= 500) {
        enableNotification(error.response.data.message, "error");
        return;
      }
    },
  });

  const loginMutation = useMutation({
    mutationFn: loginUser,
    onSuccess: (response) => {
      setActiveRequest(false);
      setUserId(response.data.user.id);
      client.setQueryData(["user", response.data.user.id], response.data.user);
      getProfile();
      getSets();
      navigate(routes.DASHBOARD, { replace: true });
    },
    onError: (error) => {
      setActiveRequest(false);
      if (
        error.response.status === 401 ||
        error.response.status === 404 ||
        error.response.status >= 500
      ) {
        enableNotification(error.response.data.message, "error");
        return;
      }
      if (error.response.status === 409 || error.response.status === 406) {
        enableNotification(error.response.data.message, "info");
        return;
      }
    },
  });

  const RecoverPassMutation = useMutation({
    mutationFn: recoverPassword,
    onSuccess: (response) => {
      setActiveRequest(false);
      enableNotification(response.data.message, "success");
    },
    onError: (error) => {
      setActiveRequest(false);
      let type = "error";
      if (error.response.status === 404) type = "info";
      enableNotification(error.response.data.message, type);
    },
  });

  const ResetPasswordMutation = useMutation({
    mutationFn: resetPassword,
    onSuccess: (response) => {
      setActiveRequest(false);
      enableNotification(response.data.message, "success");
      setTimeout(() => {
        navigate(routes.LOGIN);
      }, 3000);
    },
    onError: (error) => {
      let type = "error";
      if (error.response.status === 404) type = "info";
      enableNotification(error.response.data.message, type);
      setActiveRequest(false);
    },
  });

  const { data: user, isPending: isUserPending } = useQuery({
    queryKey: ["user", userId],
    queryFn: ({ signal }) => getUser({ signal }),
    retry: false,
    staleTime: 30 * 60 * 1000,
  });

  const {
    data: profile,
    isPending: isProfilePending,
    refetch: getProfile,
  } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: ({ signal }) => getAthleteProfile({ userId: user?.id, signal }),
    retry: false,
    enabled: user !== null,
    staleTime: 30 * 60 * 1000,
  });

  const {
    data: settings,
    isPending: isSettingsPending,
    refetch: getSets,
  } = useQuery({
    queryKey: ["settings", user?.id],
    queryFn: ({ signal }) => getSettings({ signal }),
    retry: false,
    enabled: user !== null,
    staleTime: 30 * 60 * 1000,
  });

  let contextData = {
    user,
    profile,
    settings,
    ResetPassword: ResetPasswordMutation,
    activeRequest,
    setActiveRequest,
    loginMutation,
    createAccount,
    registerModalActive,
    setRegisterModalActive,
    RecoverPassMutation,
    logoutUser,
  };

  return (
    <AuthContext.Provider value={contextData}>
      {isUserPending || isSettingsPending || isProfilePending ? (
        <AllCenteredColumn>
          <Loader height={"100vh"} />
        </AllCenteredColumn>
      ) : (
        <Box>{children}</Box>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
